import { Avatar } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { OwnerGroupData } from '@/hooks/objectives/useObjectiveOwners';
import type { User } from '@/types/directory/User';

import ObjectiveCard from '@/components/objectives/ObjectiveCard';
import { GROUP_OWNER_NAME } from '@/components/objectives/objectiveStyles';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

interface OwnerGroupProps {
    group: OwnerGroupData;
    employeesById: Map<string, User>;
    /**
     * Line under the owner's name. The objectives page shows the selected period there, like
     * the prototype's "Cycle : Q1"; the pillar page falls back to the objective count.
     */
    subtitle?: string;
}

export default function OwnerGroup({ group, employeesById, subtitle }: OwnerGroupProps) {
    const { t } = useTranslation();
    const displayName = group.owner
        ? getFullName(group.owner)
        : t('objectives.byOwner.unknownOwner');

    return (
        <div
            className={'flex flex-col gap-6'}
            data-testid={`owner-group-${group.ownerId ?? 'none'}`}
        >
            {/* Bare heading on the section surface -- no card of its own, avatar included. */}
            <div className={'flex items-center gap-3'}>
                <Avatar
                    src={getProfilePictureUrl(group.owner?.profilePictureUrl)}
                    alt={displayName}
                    initials={group.owner ? getInitials(group.owner) : undefined}
                    size={'2xs'}
                    bgColor={'gradient'}
                />

                <div>
                    <h2 className={GROUP_OWNER_NAME}>{displayName}</h2>

                    <p className={'text-obj-slate text-[12px] font-medium'}>
                        {subtitle ??
                            t('objectives.byOwner.objectiveCount', {
                                count: group.objectives.length,
                            })}
                    </p>
                </div>
            </div>

            <div className={'flex flex-col gap-6'}>
                {group.objectives.map((objective) => (
                    <ObjectiveCard
                        key={objective.id}
                        objective={objective}
                        employeesById={employeesById}
                    />
                ))}
            </div>
        </div>
    );
}
